import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  AutoAwesome,
  Delete as DeleteIcon,
  Download,
  FilterListOff,
  Refresh,
  Save as SaveIcon,
  UploadFile,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { DriverFormFields } from '../components/DriverFormFields';
import { TableFilterCell } from '../components/TableFilterCell';
import { customerAccountApi, documentApi, driverApi } from '../services/api';
import { normalizeLanguage } from '../i18n/languages';
import { CustomerAccountRecord } from '../types/customerAccount';
import { DocumentExtractionResult, ManagedDocument } from '../types/document';
import { DriverRecord, DriverSaveRequest, createEmptyDriverForm } from '../types/driver';
import { compressImageForUpload } from '../utils/imageCompression';
import { formatDateTime } from '../utils/formatters';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

type LicenseUploadSide = 'front' | 'back';
type LicenseProcessingState = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed' | 'skipped';

const toolbarButtonSx = {
  minWidth: 'auto',
  px: 2,
  py: 0.75,
  textTransform: 'none',
  fontSize: '0.875rem',
};

const toDriverForm = (driver: DriverRecord): DriverSaveRequest => ({
  firstName: driver.firstName,
  lastName: driver.lastName,
  email: driver.email || '',
  phoneNumber: driver.phoneNumber || '',
  dateOfBirth: driver.dateOfBirth || '',
  personalIdNumber: driver.personalIdNumber || '',
  driverLicenseNumber: driver.driverLicenseNumber || '',
  licenseIssueDate: driver.licenseIssueDate || '',
  licenseExpiryDate: driver.licenseExpiryDate || '',
  licenseCategories: driver.licenseCategories || '',
  notes: driver.notes || '',
  preferredLanguage: normalizeLanguage(driver.preferredLanguage || 'en'),
  emailNotifications: driver.emailNotifications ?? false,
  smsNotifications: driver.smsNotifications ?? false,
  status: driver.status || 'ACTIVE',
  linkedCustomerId: driver.linkedCustomer?.id ?? null,
});

const getDriverLinkedCustomerNames = (
  driver: DriverRecord,
  options?: { excludeDriverName?: boolean }
) => {
  const driverName = (driver.fullName || `${driver.firstName || ''} ${driver.lastName || ''}`).trim();
  const rawCustomers =
    driver.linkedCustomers && driver.linkedCustomers.length > 0
      ? driver.linkedCustomers
      : driver.linkedCustomer
        ? [driver.linkedCustomer]
        : [];

  const seen = new Set<string>();
  return rawCustomers
    .map((customer) => customer.displayName?.trim() || '')
    .filter((name) => {
      if (!name) {
        return false;
      }
      if (
        options?.excludeDriverName &&
        driverName &&
        name.localeCompare(driverName, undefined, { sensitivity: 'base' }) === 0
      ) {
        return false;
      }
      const normalized = name.toLowerCase();
      if (seen.has(normalized)) {
        return false;
      }
      seen.add(normalized);
      return true;
    });
};

export const DriverManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const frontLicenseInputRef = useRef<HTMLInputElement>(null);
  const backLicenseInputRef = useRef<HTMLInputElement>(null);

  const [selectedDriver, setSelectedDriver] = useState<DriverRecord | null>(null);
  const [formData, setFormData] = useState<DriverSaveRequest>(createEmptyDriverForm());
  const [isCreating, setIsCreating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [frontLicenseFile, setFrontLicenseFile] = useState<File | null>(null);
  const [backLicenseFile, setBackLicenseFile] = useState<File | null>(null);
  const [licenseUploadProgress, setLicenseUploadProgress] = useState(0);
  const [savedDocumentUploadProgress, setSavedDocumentUploadProgress] = useState(0);
  const [licenseProcessingState, setLicenseProcessingState] = useState<LicenseProcessingState>('idle');
  const [licenseStatusMessage, setLicenseStatusMessage] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [columnFilters, setColumnFilters] = useState({
    firstName: '',
    lastName: '',
    driverLicense: '',
    phone: '',
    email: '',
    customer: '',
    status: '',
    reservations: '',
  });

  const hasEditorSelection = isCreating || Boolean(selectedDriver);
  const isLicenseBusy =
    licenseProcessingState === 'uploading' || licenseProcessingState === 'extracting';
  const linkedCustomerDisplayName = selectedDriver
    ? getDriverLinkedCustomerNames(selectedDriver, { excludeDriverName: true }).join(', ') || null
    : null;

  const { data: drivers = [], isLoading, refetch } = useQuery('drivers', () => driverApi.getAll());
  const { data: customerAccounts = [], isLoading: loadingCustomerAccounts } = useQuery(
    ['customerAccounts', 'active'],
    () => customerAccountApi.getAll(undefined, true),
    { staleTime: 60_000 }
  );
  const {
    data: driverDocuments = [],
    isLoading: isLoadingDriverDocuments,
  } = useQuery(
    ['driverDocuments', selectedDriver?.id?.toString()],
    () => {
      if (selectedDriver?.id) {
        return driverApi.getDocuments(selectedDriver.id.toString());
      }
      return Promise.resolve([]);
    },
    { enabled: openDocumentsDialog && !!selectedDriver?.id }
  );

  const clearLicenseFiles = () => {
    setFrontLicenseFile(null);
    setBackLicenseFile(null);
    if (frontLicenseInputRef.current) {
      frontLicenseInputRef.current.value = '';
    }
    if (backLicenseInputRef.current) {
      backLicenseInputRef.current.value = '';
    }
  };

  const resetLicenseWorkflow = (clearFiles = true) => {
    if (clearFiles) {
      clearLicenseFiles();
    }
    setLicenseUploadProgress(0);
    setSavedDocumentUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const applyExtractedLicenseToForm = (extraction: DocumentExtractionResult | null) => {
    const extractedLicense = extraction?.driverLicense;
    if (!extractedLicense) {
      return;
    }

    setFormData((current) => ({
      ...current,
      firstName: extractedLicense.firstName || current.firstName,
      lastName: extractedLicense.lastName || current.lastName,
      personalIdNumber: extractedLicense.personalIdNumber || current.personalIdNumber,
      dateOfBirth: extractedLicense.dateOfBirth || current.dateOfBirth,
      driverLicenseNumber: extractedLicense.licenseNumber || current.driverLicenseNumber,
      licenseIssueDate: extractedLicense.dateOfIssue || current.licenseIssueDate,
      licenseExpiryDate: extractedLicense.expiryDate || current.licenseExpiryDate,
      licenseCategories: extractedLicense.licenseCategories || current.licenseCategories,
    }));
    setIsDirty(true);
  };

  const loadDriverIntoEditor = (driver: DriverRecord) => {
    setSelectedDriver(driver);
    setFormData(toDriverForm(driver));
    setIsCreating(false);
    setIsDirty(false);
  };

  const resetForm = () => {
    setSelectedDriver(null);
    setFormData(createEmptyDriverForm());
    setIsCreating(false);
    setIsDirty(false);
    setOpenDocumentsDialog(false);
    resetLicenseWorkflow(true);
  };

  const createMutation = useMutation((data: DriverSaveRequest) => driverApi.create(data), {
    onSuccess: async (createdDriver) => {
      await queryClient.invalidateQueries('drivers');
      await queryClient.invalidateQueries('customerAccounts');
      toast.success(t('drivers.messages.created', 'Driver created successfully.'));
      loadDriverIntoEditor(createdDriver);
      if (openDocumentsDialog) {
        await queryClient.invalidateQueries(['driverDocuments', createdDriver.id.toString()]);
      }
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('drivers.messages.createFailed', 'Failed to create driver.')
      );
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: DriverSaveRequest }) => driverApi.update(id, data),
    {
      onSuccess: async (updatedDriver) => {
        await queryClient.invalidateQueries('drivers');
        await queryClient.invalidateQueries('customerAccounts');
        toast.success(t('drivers.messages.updated', 'Driver updated successfully.'));
        loadDriverIntoEditor(updatedDriver);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('drivers.messages.updateFailed', 'Failed to update driver.')
        );
      },
    }
  );

  const deleteMutation = useMutation((id: string) => driverApi.delete(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('drivers');
      await queryClient.invalidateQueries('customerAccounts');
      toast.success(t('drivers.messages.deleted', 'Driver deleted successfully.'));
      setOpenDeleteDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('drivers.messages.deleteFailed', 'Failed to delete driver.')
      );
    },
  });

  const extractLicenseMutation = useMutation(documentApi.uploadAndExtract, {
    onSuccess: (response) => {
      setLicenseUploadProgress(100);
      setLicenseStatusMessage(response.message || null);

      const nextState: LicenseProcessingState =
        response.processingStatus === 'COMPLETED'
          ? 'completed'
          : response.processingStatus === 'SKIPPED'
            ? 'skipped'
            : 'failed';
      setLicenseProcessingState(nextState);
      applyExtractedLicenseToForm(response.extraction || null);
    },
    onError: (error: any) => {
      setLicenseProcessingState('failed');
      setLicenseStatusMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t(
            'drivers.documents.extractFailed',
            'We could not process the uploaded driver license. You can still complete the form manually.'
          )
      );
    },
  });

  const uploadDocumentsMutation = useMutation(
    () => {
      if (!selectedDriver?.id || !frontLicenseFile) {
        return Promise.reject(new Error('Driver or license file missing'));
      }

      const files = [frontLicenseFile, backLicenseFile].filter((file): file is File => Boolean(file));
      return driverApi.uploadDocuments({
        driverId: selectedDriver.id.toString(),
        documentType: 'DRIVER_LICENSE',
        description: 'Driver license',
        files,
        onUploadProgress: setSavedDocumentUploadProgress,
      });
    },
    {
      onSuccess: async (createdDocuments) => {
        await queryClient.invalidateQueries(['driverDocuments', selectedDriver?.id?.toString()]);
        setSavedDocumentUploadProgress(0);
        clearLicenseFiles();
        toast.success(
          createdDocuments.length > 1
            ? t('drivers.documents.uploadSuccessMany', {
                count: createdDocuments.length,
                defaultValue: `${createdDocuments.length} documents uploaded successfully`,
              })
            : t('drivers.documents.uploadSuccessOne', {
                defaultValue: 'Document uploaded successfully',
              })
        );
      },
      onError: (error: any) => {
        setSavedDocumentUploadProgress(0);
        toast.error(
          error.response?.data?.error ||
            t('drivers.documents.uploadFailed', 'Failed to upload driver documents.')
        );
      },
    }
  );

  const deleteDocumentMutation = useMutation(
    (documentId: string) => documentApi.deleteManagedDocument(documentId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['driverDocuments', selectedDriver?.id?.toString()]);
        toast.success(t('drivers.documents.deleteSuccess', 'Document deleted successfully.'));
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('drivers.documents.deleteFailed', 'Failed to delete document.')
        );
      },
    }
  );

  const handleCreateDriver = () => {
    const openCreateEditor = () => {
      setOpenDocumentsDialog(false);
      resetLicenseWorkflow(true);
      setSelectedDriver(null);
      setFormData(createEmptyDriverForm());
      setIsCreating(true);
      setIsDirty(false);
    };

    if (isDirty) {
      setPendingAction(() => openCreateEditor);
      setShowUnsavedDialog(true);
      return;
    }

    openCreateEditor();
  };

  const handleRowClick = (driver: DriverRecord) => {
    const openEditor = () => {
      setOpenDocumentsDialog(false);
      resetLicenseWorkflow(true);
      loadDriverIntoEditor(driver);
    };

    if (isDirty) {
      setPendingAction(() => openEditor);
      setShowUnsavedDialog(true);
      return;
    }

    openEditor();
  };

  const handleSave = () => {
    if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.phoneNumber?.trim()) {
      toast.error(
        t(
          'drivers.validation.required',
          'Driver first name, last name, and phone are required.'
        )
      );
      return;
    }

    const baseDriverPayload = formData;

    if (isCreating) {
      if (!baseDriverPayload.linkedCustomerId) {
        toast.error(
          t(
            'drivers.validation.customerRequired',
            'Choose the customer this driver belongs to.'
          )
        );
        return;
      }

      createMutation.mutate(baseDriverPayload);
      return;
    }

    if (selectedDriver) {
      updateMutation.mutate({
        id: selectedDriver.id.toString(),
        data: {
          ...baseDriverPayload,
          linkedCustomerId: selectedDriver.linkedCustomer?.id ?? formData.linkedCustomerId ?? null,
        },
      });
    }
  };

  const handleOpenDocumentsDialog = () => {
    if (!hasEditorSelection) {
      return;
    }
    setOpenDocumentsDialog(true);
  };

  const handleLicenseFileSelected = async (side: LicenseUploadSide, file: File | null) => {
    const processedFile = file ? await compressImageForUpload(file) : null;
    if (side === 'front') {
      setFrontLicenseFile(processedFile);
    } else {
      setBackLicenseFile(processedFile);
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const handleRemoveLicenseFile = (side: LicenseUploadSide) => {
    if (side === 'front') {
      setFrontLicenseFile(null);
      if (frontLicenseInputRef.current) {
        frontLicenseInputRef.current.value = '';
      }
    } else {
      setBackLicenseFile(null);
      if (backLicenseInputRef.current) {
        backLicenseInputRef.current.value = '';
      }
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const handleExtractLicense = () => {
    if (!frontLicenseFile) {
      setLicenseStatusMessage(
        t('drivers.documents.frontRequired', 'Please upload the front of the driver license first.')
      );
      setLicenseProcessingState('failed');
      return;
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('uploading');
    setLicenseStatusMessage(
      t('drivers.documents.uploading', 'Uploading the document securely...')
    );

    extractLicenseMutation.mutate({
      documentType: 'DRIVER_LICENSE',
      frontFile: frontLicenseFile,
      backFile: backLicenseFile,
      onUploadProgress: (progressPercent: number) => {
        setLicenseUploadProgress(progressPercent);
        if (progressPercent >= 100) {
          setLicenseProcessingState('extracting');
          setLicenseStatusMessage(
            t(
              'drivers.documents.extracting',
              'AI is reading the driver license and extracting the fields...'
            )
          );
        } else {
          setLicenseProcessingState('uploading');
        }
      },
    });
  };

  const handleUploadDocuments = () => {
    if (!selectedDriver) {
      toast.error(
        t(
          'drivers.documents.saveDriverFirst',
          'Save the driver first before uploading the license.'
        )
      );
      return;
    }

    if (!frontLicenseFile) {
      toast.error(
        t('drivers.documents.frontRequired', 'Please upload the front of the driver license first.')
      );
      return;
    }

    uploadDocumentsMutation.mutate();
  };

  const handlePreviewDocument = async (document: ManagedDocument) => {
    try {
      const blob = await documentApi.previewManagedDocument(document.id.toString());
      const previewUrl = URL.createObjectURL(blob);
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(previewUrl), 60_000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('drivers.documents.previewFailed', 'Failed to preview document.')
      );
    }
  };

  const handleDownloadDocument = async (document: ManagedDocument) => {
    try {
      const blob = await documentApi.downloadManagedDocument(document.id.toString());
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = document.displayName || 'driver-document';
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('drivers.documents.downloadFailed', 'Failed to download document.')
      );
    }
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((current) => ({ ...current, [column]: value }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredDrivers = drivers.filter((driver) => {
    return (
      (!columnFilters.firstName ||
        driver.firstName.toLowerCase().includes(columnFilters.firstName.toLowerCase())) &&
      (!columnFilters.lastName ||
        driver.lastName.toLowerCase().includes(columnFilters.lastName.toLowerCase())) &&
      (!columnFilters.driverLicense ||
        (driver.driverLicenseNumber || '')
          .toLowerCase()
          .includes(columnFilters.driverLicense.toLowerCase())) &&
      (!columnFilters.phone ||
        (driver.phoneNumber || '').toLowerCase().includes(columnFilters.phone.toLowerCase())) &&
      (!columnFilters.email ||
        (driver.email || '').toLowerCase().includes(columnFilters.email.toLowerCase())) &&
      (!columnFilters.customer ||
        getDriverLinkedCustomerNames(driver)
          .join(', ')
          .toLowerCase()
          .includes(columnFilters.customer.toLowerCase())) &&
      (!columnFilters.status ||
        (driver.status || '').toLowerCase().includes(columnFilters.status.toLowerCase())) &&
      (!columnFilters.reservations ||
        String(driver.totalReservations || 0).includes(columnFilters.reservations))
    );
  });

  const sortedDrivers = useMemo(() => {
    if (!sortConfig) {
      return filteredDrivers;
    }

    const sorted = [...filteredDrivers];
    sorted.sort((left, right) => {
      const valueFor = (driver: DriverRecord) => {
        switch (sortConfig.key) {
          case 'firstName':
            return driver.firstName || '';
          case 'lastName':
            return driver.lastName || '';
          case 'driverLicense':
            return driver.driverLicenseNumber || '';
          case 'phone':
            return driver.phoneNumber || '';
          case 'email':
            return driver.email || '';
          case 'customer':
            return getDriverLinkedCustomerNames(driver).join(', ');
          case 'status':
            return driver.status || '';
          case 'reservations':
            return driver.totalReservations || 0;
          default:
            return '';
        }
      };

      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return sortConfig.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }
      const comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredDrivers, sortConfig]);

  const paginatedDrivers = sortedDrivers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.max(1, Math.ceil(sortedDrivers.length / rowsPerPage));
  const paginationStart = sortedDrivers.length === 0 ? 0 : page * rowsPerPage + 1;
  const paginationEnd =
    sortedDrivers.length === 0 ? 0 : Math.min((page + 1) * rowsPerPage, sortedDrivers.length);

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                sx={toolbarButtonSx}
                onClick={handleCreateDriver}
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                {t('common.new', 'New')}
              </Button>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                sx={toolbarButtonSx}
                onClick={handleSave}
                disabled={!hasEditorSelection || createMutation.isLoading || updateMutation.isLoading}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button
                startIcon={<AutoAwesome />}
                variant="outlined"
                sx={toolbarButtonSx}
                onClick={handleOpenDocumentsDialog}
                disabled={!hasEditorSelection}
              >
                {t('drivers.documents.licenseAction', 'AI Import')}
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                variant="contained"
                sx={toolbarButtonSx}
                onClick={() => setOpenDeleteDialog(true)}
                disabled={!selectedDriver || deleteMutation.isLoading}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  bgcolor: 'background.paper',
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => refetch()}
                  size="small"
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {t('common.refresh', 'Refresh')}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    disabled={page === 0}
                    sx={{
                      width: '28px',
                      height: '28px',
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}
                  >
                    <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      color: 'text.secondary',
                      minWidth: '96px',
                      textAlign: 'center',
                    }}
                  >
                    {sortedDrivers.length === 0
                      ? t('common.noResults', 'No results')
                      : `${paginationStart}-${paginationEnd} ${t('common.of')} ${sortedDrivers.length}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber: number;
                      if (totalPages <= 5) {
                        pageNumber = index;
                      } else if (page < 2) {
                        pageNumber = index;
                      } else if (page >= totalPages - 2) {
                        pageNumber = totalPages - 5 + index;
                      } else {
                        pageNumber = page - 2 + index;
                      }

                      if (pageNumber < 0 || pageNumber >= totalPages) {
                        return null;
                      }

                      return (
                        <Button
                          key={pageNumber}
                          size="small"
                          onClick={() => setPage(pageNumber)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            px: 1,
                            fontSize: '0.8125rem',
                            bgcolor: page === pageNumber ? 'primary.main' : 'transparent',
                            color: page === pageNumber ? 'white' : 'text.primary',
                            '&:hover': {
                              bgcolor: page === pageNumber ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {pageNumber + 1}
                        </Button>
                      );
                    })}
                    {totalPages > 5 && page < totalPages - 3 ? (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}
                      >
                        ...
                      </Typography>
                    ) : null}
                    {totalPages > 5 && page < totalPages - 3 ? (
                      <Button
                        size="small"
                        onClick={() => setPage(totalPages - 1)}
                        sx={{
                          minWidth: '32px',
                          height: '28px',
                          px: 1,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {totalPages}
                      </Button>
                    ) : null}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                    sx={{
                      width: '28px',
                      height: '28px',
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}
                  >
                    <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Select
                    value={rowsPerPage}
                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    size="small"
                    sx={{
                      height: '28px',
                      fontSize: '0.8125rem',
                      ml: 1,
                      '& .MuiSelect-select': { py: 0.5, px: 1 },
                    }}
                    renderValue={(value) =>
                      t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })
                    }
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  startIcon={<FilterListOff fontSize="small" />}
                  onClick={() => {
                    setColumnFilters({
                      firstName: '',
                      lastName: '',
                      driverLicense: '',
                      phone: '',
                      email: '',
                      customer: '',
                      status: '',
                      reservations: '',
                    });
                    setSortConfig(null);
                    setPage(0);
                  }}
                  sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Toolbar>

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      fontSize: '0.875rem',
                      px: { xs: 2, sm: 3 },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableFilterCell
                        placeholder={t('fields.firstName')}
                        value={columnFilters.firstName}
                        onChange={(value) => handleColumnFilterChange('firstName', value)}
                        onSort={() => handleSort('firstName')}
                        sortDirection={sortConfig?.key === 'firstName' ? sortConfig.direction : null}
                        width="12%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.lastName')}
                        value={columnFilters.lastName}
                        onChange={(value) => handleColumnFilterChange('lastName', value)}
                        onSort={() => handleSort('lastName')}
                        sortDirection={sortConfig?.key === 'lastName' ? sortConfig.direction : null}
                        width="12%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.licenseNumber')}
                        value={columnFilters.driverLicense}
                        onChange={(value) => handleColumnFilterChange('driverLicense', value)}
                        onSort={() => handleSort('driverLicense')}
                        sortDirection={sortConfig?.key === 'driverLicense' ? sortConfig.direction : null}
                        width="14%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.phoneNumber')}
                        value={columnFilters.phone}
                        onChange={(value) => handleColumnFilterChange('phone', value)}
                        onSort={() => handleSort('phone')}
                        sortDirection={sortConfig?.key === 'phone' ? sortConfig.direction : null}
                        width="14%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.email')}
                        value={columnFilters.email}
                        onChange={(value) => handleColumnFilterChange('email', value)}
                        onSort={() => handleSort('email')}
                        sortDirection={sortConfig?.key === 'email' ? sortConfig.direction : null}
                        width="18%"
                      />
                      <TableFilterCell
                        placeholder={t('nav.customers', 'Customers')}
                        value={columnFilters.customer}
                        onChange={(value) => handleColumnFilterChange('customer', value)}
                        onSort={() => handleSort('customer')}
                        sortDirection={sortConfig?.key === 'customer' ? sortConfig.direction : null}
                        width="16%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.status')}
                        value={columnFilters.status}
                        onChange={(value) => handleColumnFilterChange('status', value)}
                        onSort={() => handleSort('status')}
                        sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null}
                        width="8%"
                      />
                      <TableFilterCell
                        placeholder={t('billingProfiles.fields.rentals', 'Rentals')}
                        value={columnFilters.reservations}
                        onChange={(value) => handleColumnFilterChange('reservations', value)}
                        onSort={() => handleSort('reservations')}
                        sortDirection={sortConfig?.key === 'reservations' ? sortConfig.direction : null}
                        width="10%"
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedDrivers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          {t('drivers.empty', 'No drivers found.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedDrivers.map((driver) => (
                        <TableRow
                          key={driver.id}
                          hover
                          selected={selectedDriver?.id === driver.id && !isCreating}
                          onClick={() => handleRowClick(driver)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&.Mui-selected': { bgcolor: 'action.selected' },
                          }}
                        >
                          <TableCell sx={{ py: 0.75 }}>{driver.firstName}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>{driver.lastName}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {driver.driverLicenseNumber || t('common.unavailable', 'N/A')}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {driver.phoneNumber || t('common.unavailable', 'N/A')}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {driver.email || t('common.unavailable', 'N/A')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getDriverLinkedCustomerNames(driver).join(', ') ||
                                t('drivers.unassigned', 'Unassigned')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {driver.status === 'ACTIVE'
                              ? t('common.active', 'Active')
                              : driver.status === 'INACTIVE'
                                ? t('common.inactive', 'Inactive')
                                : driver.status === 'SUSPENDED'
                                  ? t('fields.suspended', 'Suspended')
                                  : t('fields.banned', 'Banned')}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>{driver.totalReservations || 0}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {hasEditorSelection ? (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 1.5, p: 1.5, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    {isCreating
                      ? t('drivers.newDriverTitle', 'New Driver')
                      : selectedDriver?.fullName || t('drivers.title', 'Drivers')}
                  </Typography>
                  {!isCreating && linkedCustomerDisplayName ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                      {linkedCustomerDisplayName}
                    </Typography>
                  ) : null}
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {isCreating ? (
                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small" disabled={createMutation.isLoading}>
                        <InputLabel>{t('nav.customers', 'Customers')}</InputLabel>
                        <Select
                          label={t('nav.customers', 'Customers')}
                          value={formData.linkedCustomerId ?? ''}
                          onChange={(event) => {
                            const nextValue =
                              event.target.value === '' ? null : Number(event.target.value);
                            setFormData((current) => ({
                              ...current,
                              linkedCustomerId: nextValue,
                            }));
                            setIsDirty(true);
                          }}
                        >
                          <MenuItem value="">
                            {loadingCustomerAccounts
                              ? t('common.loading', 'Loading...')
                              : t('common.select', 'Select')}
                          </MenuItem>
                          {customerAccounts.map((customerAccount: CustomerAccountRecord) => (
                            <MenuItem key={customerAccount.id} value={customerAccount.id}>
                              {customerAccount.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                ) : null}

                <DriverFormFields
                  value={formData}
                  disabled={
                    createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading
                  }
                  layout="compact"
                  onChange={(nextValue) => {
                    setFormData(nextValue);
                    setIsDirty(true);
                  }}
                />
              </Paper>
            </Grid>
          ) : null}
        </Grid>
      </Container>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('drivers.deleteTitle', 'Delete Driver')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('drivers.deleteBody', {
              defaultValue: 'Delete "{{name}}"?',
              name: selectedDriver?.fullName || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            color="error"
            onClick={() => selectedDriver && deleteMutation.mutate(selectedDriver.id.toString())}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDocumentsDialog}
        onClose={() => setOpenDocumentsDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('drivers.documents.title', 'Driver License')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                variant="outlined"
                disabled={isLicenseBusy || uploadDocumentsMutation.isLoading}
              >
                {t('drivers.documents.frontFile', 'Front of License')}
                <input
                  ref={frontLicenseInputRef}
                  hidden
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(event) =>
                    handleLicenseFileSelected('front', event.target.files?.[0] || null)
                  }
                />
              </Button>
              {frontLicenseFile ? (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {frontLicenseFile.name}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleRemoveLicenseFile('front')}
                    disabled={isLicenseBusy || uploadDocumentsMutation.isLoading}
                  >
                    {t('common.delete', 'Delete')}
                  </Button>
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                variant="outlined"
                disabled={isLicenseBusy || uploadDocumentsMutation.isLoading}
              >
                {t('drivers.documents.backFile', 'Back of License')}
                <input
                  ref={backLicenseInputRef}
                  hidden
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(event) =>
                    handleLicenseFileSelected('back', event.target.files?.[0] || null)
                  }
                />
              </Button>
              {backLicenseFile ? (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {backLicenseFile.name}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleRemoveLicenseFile('back')}
                    disabled={isLicenseBusy || uploadDocumentsMutation.isLoading}
                  >
                    {t('common.delete', 'Delete')}
                  </Button>
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={
                    isLicenseBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />
                  }
                  disabled={!frontLicenseFile || isLicenseBusy || uploadDocumentsMutation.isLoading}
                  onClick={handleExtractLicense}
                >
                  {isLicenseBusy
                    ? t('drivers.documents.extracting', 'Extracting...')
                    : t('drivers.documents.extractAction', 'Extract with AI')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFile />}
                  disabled={!selectedDriver || !frontLicenseFile || uploadDocumentsMutation.isLoading}
                  onClick={handleUploadDocuments}
                >
                  {uploadDocumentsMutation.isLoading
                    ? t('common.uploading', 'Uploading...')
                    : t('drivers.documents.uploadLicense', 'Upload License')}
                </Button>
              </Box>
              {!selectedDriver && isCreating ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t(
                    'drivers.documents.saveDriverFirst',
                    'Save the driver first before uploading the license.'
                  )}
                </Typography>
              ) : null}
            </Grid>
          </Grid>

          {licenseStatusMessage ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {licenseStatusMessage}
            </Typography>
          ) : null}

          {isLicenseBusy ? (
            <LinearProgress
              sx={{ mb: 2 }}
              variant={licenseProcessingState === 'extracting' ? 'indeterminate' : 'determinate'}
              value={licenseProcessingState === 'extracting' ? undefined : licenseUploadProgress}
            />
          ) : null}

          {uploadDocumentsMutation.isLoading ? (
            <LinearProgress
              sx={{ mb: 2 }}
              variant={savedDocumentUploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={savedDocumentUploadProgress}
            />
          ) : null}

          {selectedDriver ? (
            isLoadingDriverDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : driverDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('drivers.documents.empty', 'No documents uploaded for this driver yet.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('fields.name', 'Name')}</TableCell>
                      <TableCell>{t('fields.type', 'Type')}</TableCell>
                      <TableCell>{t('fields.createdAt', 'Created')}</TableCell>
                      <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {driverDocuments.map((document) => (
                      <TableRow key={document.id}>
                        <TableCell>{document.displayName}</TableCell>
                        <TableCell>{document.documentTypeLabel || document.documentType}</TableCell>
                        <TableCell>{document.createdAt ? formatDateTime(document.createdAt) : '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('common.preview', 'Preview')}>
                            <Button size="small" onClick={() => handlePreviewDocument(document)} startIcon={<ViewIcon />}>
                              {t('common.preview', 'Preview')}
                            </Button>
                          </Tooltip>
                          <Tooltip title={t('common.download', 'Download')}>
                            <Button size="small" onClick={() => handleDownloadDocument(document)} startIcon={<Download />}>
                              {t('common.download', 'Download')}
                            </Button>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => deleteDocumentMutation.mutate(document.id.toString())}
                              startIcon={<DeleteIcon />}
                            >
                              {t('common.delete')}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocumentsDialog(false)}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)}>
        <DialogTitle>{t('common.unsavedChanges', 'Unsaved Changes')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'common.unsavedChangesPrompt',
              'You have unsaved changes. Do you want to discard them and continue?'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="warning"
            onClick={() => {
              setShowUnsavedDialog(false);
              setIsDirty(false);
              pendingAction?.();
              setPendingAction(null);
            }}
          >
            {t('common.discardChanges', 'Discard Changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
